// The pedlar's cart on the Commons plaza (P81, plazmi.merchant): on
// Wednesdays a travelling cart stands by the fountain with three things on
// it, the cart's own piece in the middle. /api/commons says whether it's
// here (`cart.here`) and what it carries.
import * as THREE from 'three';
import { mat, cyl, sphere, rbox, at } from './kit.js';
import { ITEMS } from './catalog.js';
import { letters } from './place.js';

export type CartThing = { id: string; name: string; render: string; coins: number; note?: string | null; cart_only: boolean; gone_home: number };
export type Cart = { here: boolean; line: string; things?: CartThing[] };

// where it stands: west of the fountain, facing south, between the cherry and the fountain
export const CART_AT: [number, number] = [34.8, 29.8];
export const CART_FOOT = { w: 4.0, d: 2.4 };

function stripes(a: string, b: string) {
  const c = document.createElement('canvas'); c.width = 64; c.height = 8;
  const x = c.getContext('2d')!;
  for (let i = 0; i < 8; i++) { x.fillStyle = i % 2 ? b : a; x.fillRect(i * 8, 0, 8, 8); }
  const t = new THREE.CanvasTexture(c); t.colorSpace = THREE.SRGBColorSpace; t.wrapS = t.wrapT = THREE.RepeatWrapping;
  return t;
}

// scaled to stand within `size` metres, its feet at 0
function fitted(o: THREE.Object3D, size: number) {
  const b = new THREE.Box3().setFromObject(o), s = new THREE.Vector3(); b.getSize(s);
  const k = size / Math.max(0.01, s.x, s.y, s.z);
  o.scale.multiplyScalar(k);
  const g = new THREE.Group(); g.add(o); o.position.y = -b.min.y * k;
  return g;
}

function tag(text: string) {
  const m = new THREE.Mesh(new THREE.PlaneGeometry(0.34, 0.2),
    new THREE.MeshStandardMaterial({ map: letters([{ text, size: 64, color: '#3a2a20' }], 170, 100, '#fffaf0'), roughness: 0.9 }));
  m.rotation.x = -0.3;
  return m;
}

function wheel() {
  const g = new THREE.Group(), wood = mat('#4f7a52', { rough: 0.7 }), rim = mat('#2e3b33', { rough: 0.5, metal: 0.4 });
  const tyre = new THREE.Mesh(new THREE.TorusGeometry(0.5, 0.045, 8, 28), rim); g.add(tyre);
  const hub = cyl(0.08, 0.08, 0.14, wood, 12); hub.rotation.x = Math.PI / 2; g.add(hub);
  for (let i = 0; i < 8; i++) { const sp = cyl(0.018, 0.018, 0.92, wood, 5); sp.rotation.z = (i / 8) * Math.PI; g.add(sp); }
  return g;
}

export function cartModel(c: Cart) {
  const g = new THREE.Group();
  const body = mat('#3f6f58', { rough: 0.7 }), wood = mat('#8a6343', { rough: 0.8 }), cream = mat('#f3e7cf', { rough: 0.8 }), brass = mat('#d6b45a', { rough: 0.3, metal: 0.8 });
  // the bed of the cart on its axle, two big green wheels
  g.add(at(rbox(2.6, 0.5, 1.3, 0.04, body, 2), 0, 0.85, 0));
  g.add(at(rbox(2.64, 0.06, 1.34, 0.02, cream, 1), 0, 1.12, 0));
  g.add(at(rbox(2.5, 0.06, 0.02, 0.01, mat('#d9a441'), 1), 0, 0.85, 0.66));
  for (const z of [-0.72, 0.72]) g.add(at(wheel(), 0.1, 0.55, z));
  const axle = cyl(0.035, 0.035, 1.6, mat('#2e3b33'), 6); axle.rotation.x = Math.PI / 2; axle.position.set(0.1, 0.55, 0); g.add(axle);
  // shafts resting on the ground, and a prop leg at the back
  for (const z of [-0.45, 0.45]) { const s = cyl(0.035, 0.035, 1.9, wood, 6); s.rotation.z = Math.PI / 2 + 0.38; s.position.set(2.05, 0.46, z); g.add(s); }
  g.add(at(cyl(0.04, 0.04, 0.62, wood, 6), -1.15, 0.31, 0));
  // the canopy: tall posts at the back and a striped awning leaning out over the goods, open at the front so they show
  for (const x of [-1.25, 1.25]) g.add(at(cyl(0.035, 0.035, 1.75, wood, 6), x, 2.0, -0.6));
  const tex = stripes('#c4664c', '#f6ead2'); tex.repeat.set(2, 1);
  const awn = new THREE.Mesh(new THREE.PlaneGeometry(2.8, 1.0), new THREE.MeshStandardMaterial({ map: tex, side: THREE.DoubleSide, roughness: 0.85 }));
  awn.rotation.x = -Math.PI / 2 + 0.5; awn.position.set(0, 2.66, -0.18); g.add(awn);
  // a scalloped valance along its front edge, and a bell on a hook
  for (let i = 0; i < 10; i++) {
    const sc = new THREE.Mesh(new THREE.CircleGeometry(0.13, 12, Math.PI, Math.PI), mat(i % 2 ? '#f6ead2' : '#c4664c', { rough: 0.85, side: THREE.DoubleSide }));
    sc.position.set(-1.4 + (i + 0.5) * 0.28, 2.43, 0.26); g.add(sc);
  }
  const bell = sphere(0.07, brass); bell.scale.y = 1.2; bell.position.set(1.32, 2.25, 0.2); g.add(bell);
  // the sign board above
  const board = new THREE.Mesh(new THREE.PlaneGeometry(1.6, 0.36),
    new THREE.MeshStandardMaterial({ map: letters([{ text: "The pedlar's cart", size: 56, color: '#3a2a20' }], 512, 116, '#f6e9c8'), roughness: 0.9, side: THREE.DoubleSide }));
  board.position.set(0, 3.12, -0.62); g.add(board);
  // what's on it: the cart's own piece in the middle, raised a little
  const things = (c.things || []).slice(0, 3);
  const order = [things[1], things[0], things[2]];
  order.forEach((t, i) => {
    if (!t) return;
    const x = -0.88 + i * 0.88, own = t.cart_only;
    let o: THREE.Object3D | null = null;
    const it = (ITEMS as any)[t.render];
    if (it) { try { o = fitted(it.build(), own ? 1.15 : 0.85); } catch { o = null; } }
    if (!o) o = rbox(0.3, 0.22, 0.26, 0.02, mat('#c9a77a', { rough: 0.95 }), 2);
    o.position.set(x, 1.15 + (own ? 0.08 : 0), 0.1); g.add(o);
    if (own) g.add(at(rbox(0.5, 0.08, 0.5, 0.02, cream, 1), x, 1.17, 0.1));
    const tg = tag(`${t.coins}`); tg.position.set(x, 1.24, 0.6); g.add(tg);
  });
  g.userData.cart = c;
  return g;
}
